import React from 'react';
import { useTranslation } from 'react-i18next';
import { FileCode, FileArchive, Download, Trash2, Package } from 'lucide-react';
import { formatFileSize } from '../utils/formatFileSize';

const ICON_MAP = {
  mq5: FileCode,
  ex5: FileCode,
  zip: FileArchive,
  rar: FileArchive,
};

const COLOR_MAP = {
  mq5: 'bg-blue-100 text-blue-700',
  ex5: 'bg-blue-100 text-blue-700',
  zip: 'bg-amber-100 text-amber-700',
  rar: 'bg-orange-100 text-orange-700',
};

const PluginCard = ({ plugin, onDownload, onDelete, downloading, deleting }) => {
  const { t, i18n } = useTranslation();
  const Icon = ICON_MAP[plugin.extension] || Package;
  const colorClass = COLOR_MAP[plugin.extension] || 'bg-gray-100 text-gray-700';

  const uploadDate = new Date(plugin.uploadedAt).toLocaleDateString(
    i18n.language === 'az' ? 'az-AZ' : i18n.language,
    { year: 'numeric', month: 'short', day: 'numeric' }
  );

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 hover:shadow-md hover:border-primary-200 transition-all p-5 flex flex-col gap-3 min-h-[200px] group">
      <div className="flex items-start justify-between gap-2">
        <div className={`w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0 ${colorClass}`}>
          <Icon className="w-6 h-6" />
        </div>
        <span className="inline-block text-[10px] font-mono bg-gray-100 text-gray-600 px-2 py-1 rounded uppercase">
          {plugin.extension}
        </span>
      </div>

      <div className="flex-1 min-w-0 space-y-1">
        <h3
          className="text-base font-bold text-gray-900 truncate"
          title={plugin.name}
        >
          {plugin.name}
        </h3>
        <p className="text-xs text-gray-500 truncate" title={plugin.originalName}>
          {plugin.originalName}
        </p>
        {plugin.description && (
          <p
            className="text-xs text-gray-600 line-clamp-2"
            title={plugin.description}
          >
            {plugin.description}
          </p>
        )}
        <div className="flex items-center gap-2 mt-2">
          <span className="inline-block text-xs font-mono bg-primary-50 text-primary-700 px-2 py-0.5 rounded">
            #{plugin.displayId}
          </span>
          <span className="text-xs text-gray-500">
            {formatFileSize(plugin.size)} • {uploadDate}
          </span>
        </div>
      </div>

      <div className="flex items-center justify-end gap-2 border-t border-gray-100 pt-3 mt-auto">
        <button
          onClick={() => onDownload(plugin)}
          disabled={downloading || deleting}
          className="p-2 rounded-lg text-primary-600 hover:bg-primary-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          title={t('plugins.action.download')}
        >
          {downloading ? (
            <span className="inline-block w-4 h-4 border-2 border-primary-600 border-t-transparent rounded-full animate-spin" />
          ) : (
            <Download className="w-4 h-4" />
          )}
        </button>
        <button
          onClick={() => onDelete(plugin)}
          disabled={downloading || deleting}
          className="p-2 rounded-lg text-red-600 hover:bg-red-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          title={t('plugins.action.delete')}
        >
          {deleting ? (
            <span className="inline-block w-4 h-4 border-2 border-red-600 border-t-transparent rounded-full animate-spin" />
          ) : (
            <Trash2 className="w-4 h-4" />
          )}
        </button>
      </div>
    </div>
  );
};

export default PluginCard;